<template>
  <div class="contents_855">
    <div v-show="isInputStep">
      <h1 class="title2">お客様情報編集</h1>
      <div class="mg20">
        <InputFormIndividual
          v-if="!isCompany"
          :show-password="false"
          :show-contract-type="false"
          :show-payment-method="false"
          :show-contact-guidance="false"
        />
        <InputFormCompany
          v-else
          :show-password="false"
          :show-contract-type="false"
          :show-payment-method="false"
          :show-contact-guidance="false"
        />

        <div class="err-msg-area" v-show="hasError">
          <span class="font_orange">
            {{ $t('hasErrors') }}
          </span>
        </div>

        <p class="submit_bx_l">
          <button @click="goBack" class="submit1 bg_gray">
            {{ $t('backButton') }}
          </button>
        </p>
        <p class="submit_bx_r">
          <button @click="goNext" class="submit1 bg_green">
            確認画面
          </button>
        </p>
      </div>
    </div>

    <div v-show="isConfirmStep">
      <h1 class="title2">お客様情報編集</h1>
      <div class="contents_box mg20">
        <p class="mypage_notes mg30 mg_bt30 font_orange">
          下記の情報でよろしければ送信してください。
        </p>
        <ReadonlyViewIndividual
          v-if="!isCompany"
          :show-payment-method="false"
          :show-contact-guidance="false"
        />
        <ReadonlyViewCompany
          v-else
          :show-payment-method="false"
          :show-contact-guidance="false"
        />

        <div class="err-msg-area" v-show="editState.serverErrors.length">
          <p v-for="(e, i) in editState.serverErrors" :key="i">
            <span class="font_orange">{{ e }}</span>
          </p>
        </div>

        <p class="submit_bx_l">
          <button @click="prevStep" class="submit1 bg_gray">
            戻る
          </button>
        </p>
        <p class="submit_bx_r">
          <button @click="save" class="submit1 bg_green" :disabled="editState.saving">
            保存
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, onMounted, reactive } from 'vue'
import adminUserApi from '@/api/adminUser'
import areaInfoApi from '@/api/areaInfo'
import InputFormCompany from '@/components/lib/user/components/InputFormCompany.vue'
import InputFormIndividual from '@/components/lib/user/components/InputFormIndividual.vue'
import ReadonlyViewCompany from '@/components/lib/user/components/ReadonlyViewCompany.vue'
import ReadonlyViewIndividual from '@/components/lib/user/components/ReadonlyViewIndividual.vue'
import { fillUserStateWithMe, provideUserState } from '@/components/lib/user/composables/useUserState'
import { provideUserErrorState } from '@/components/lib/user/composables/useUserErrorState'
import { provideCommonState } from '@/components/ProfileEdit/composables/useCommonState'
import { provideMaster } from '@/composables/useMaster'
import { useAdminPageAccess } from '@/composables/useAdminPageAccess'
import { validateUserParams } from '@/validations/userValidations'
import { convertUserDataForSave } from '@/utils/userUtils'
import { AdminPageType } from '@/consts/admin'

export default defineComponent({
  name: 'admin-customer-profile-edit',
  components: {
    InputFormCompany,
    InputFormIndividual,
    ReadonlyViewCompany,
    ReadonlyViewIndividual,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    adminType: {
      type: String,
      default: AdminPageType.ADMIN,
    },
  },
  setup(props) {
    const vue = getCurrentInstance().proxy
    const { ensureManagerAccess } = useAdminPageAccess(props.adminType)
    const commonState = provideCommonState()
    const userState = provideUserState()
    const userErrorState = provideUserErrorState()
    provideMaster()
    const StepKey = {
      INPUT: 'input',
      CONFIRM: 'confirm',
    }

    const editState = reactive({
      currentStep: StepKey.INPUT,
      saving: false,
      serverErrors: [],
    })

    const isInputStep = computed(() => {
      return editState.currentStep === StepKey.INPUT
    })
    const isConfirmStep = computed(() => {
      return editState.currentStep === StepKey.CONFIRM
    })
    const isCompany = computed(() => {
      return userState.is_company
    })
    const hasError = computed(() => {
      return Object.keys(userErrorState.errors).length > 0
    })

    onMounted(async () => {
      const canAccess = await ensureManagerAccess()
      if (!canAccess) return

      const [{ data: customer }, { data: areaInfos }] = await Promise.all([
        adminUserApi.getUser(props.id),
        areaInfoApi.index(),
      ])
      fillUserStateWithMe(userState, customer)
      commonState.areaInfos = areaInfos
    })

    function validate() {
      userErrorState.errors = {}

      const [isUserParamsOk, errors] = validateUserParams(
        userState,
        userState.user_plan ? userState.user_plan.plan_id : null,
        commonState.areaInfos,
        { noPasswordCheck: true }
      )
      if (!isUserParamsOk) {
        userErrorState.errors = errors
        return false
      }
      return true
    }

    function buildPayload() {
      const payload = {
        name1: userState.name1,
        name2: userState.name2,
        kana1: userState.kana1,
        kana2: userState.kana2,
        bill_addr_zipcode: userState.bill_addr_zipcode,
        bill_addr_detail1: userState.bill_addr_detail1,
        bill_addr_detail2: userState.bill_addr_detail2,
        bill_addr_detail3: userState.bill_addr_detail3,
        send_addr_name: userState.send_addr_name,
        send_addr_zipcode: userState.send_addr_zipcode,
        send_addr_detail1: userState.send_addr_detail1,
        send_addr_detail2: userState.send_addr_detail2,
        send_addr_detail3: userState.send_addr_detail3,
        phone_number: userState.phone_number,
        email: userState.email,
      }
      if (!userState.is_company) {
        return payload
      }
      return {
        ...payload,
        email2: userState.email2,
        company_name: userState.company_name,
        company_name_kana: userState.company_name_kana,
        company_hq_phone_number: userState.company_hq_phone_number,
        company_ceo_name1: userState.company_ceo_name1,
        company_ceo_name2: userState.company_ceo_name2,
        company_ceo_name_kana1: userState.company_ceo_name_kana1,
        company_ceo_name_kana2: userState.company_ceo_name_kana2,
      }
    }

    function goBack() {
      vue.$router.back()
    }

    function goNext() {
      convertUserDataForSave(userState)

      if (!validate()) { return }
      editState.currentStep = StepKey.CONFIRM
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 0)
    }

    function prevStep() {
      editState.currentStep = StepKey.INPUT
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 0)
    }

    async function save() {
      editState.saving = true
      editState.serverErrors = []
      try {
        await adminUserApi.updateCustomerProfile(props.id, buildPayload())
        goBack()
      } catch (e) {
        if (e && e.response && e.response.data) {
          const data = e.response.data
          if (Array.isArray(data)) {
            editState.serverErrors = data
          } else if (data.errors) {
            let errors = []
            Object.keys(data.errors).forEach(k => {
              errors = errors.concat(data.errors[k])
            })
            editState.serverErrors = errors
          } else if (data.message) {
            editState.serverErrors = [data.message]
          }
        }
      } finally {
        editState.saving = false
      }
    }

    return {
      editState,
      isInputStep,
      isConfirmStep,
      isCompany,
      hasError,
      goBack,
      goNext,
      prevStep,
      save,
    }
  }
})
</script>

<style lang="scss" scoped>
.err-msg-area {
  margin: 30px auto 0;
  text-align: center;
  width: 90%;
}
</style>
